// pages/ThemesGeneriquesPage.jsx
// Page de gestion des thèmes de grammaire et leurs génériques associés

import React, { useState, useEffect } from 'react';
import {
  Loader2,
  Edit2,
  Check,
  X,
  Plus,
  AlertTriangle,
  Link as LinkIcon,
} from 'lucide-react';
import { getLangueFlag } from '../utils/exerciseUtils';
import './ThemesGeneriquesPage.css';

export default function ThemesGeneriquesPage() {
  const [langue, setLangue] = useState('en');
  const [themes, setThemes] = useState([]);
  const [generiques, setGeneriques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Édition générique existant
  const [editingGenId, setEditingGenId] = useState(null);
  const [editGenForm, setEditGenForm] = useState({ generique: '', explication: '' });

  // Création nouveau générique
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({ generique: '', explication: '' });

  // Chargement des données
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/exercices/quiz-grammaire/themes-generiques?langue=${langue}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors du chargement');
      }

      setThemes(data.themes || []);
      setGeneriques(data.generiques || []);
    } catch (err) {
      console.error('Erreur chargement themes-generiques:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [langue]); // eslint-disable-line react-hooks/exhaustive-deps

  // ========== Actions Génériques ==========

  const handleCreateGenerique = async () => {
    if (!createForm.generique.trim()) return;

    try {
      const response = await fetch('/api/exercices/quiz-grammaire/generiques', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          langue,
          generique: createForm.generique,
          explication: createForm.explication,
        }),
      });
      const data = await response.json();

      if (data.success) {
        setShowCreateForm(false);
        setCreateForm({ generique: '', explication: '' });
        loadData();
      } else {
        alert('Erreur : ' + (data.error || 'Erreur inconnue'));
      }
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  const startEditingGenerique = (gen) => {
    setEditingGenId(gen.id);
    setEditGenForm({ generique: gen.generique, explication: gen.explication || '' });
  };

  const handleSaveGenerique = async () => {
    if (!editGenForm.generique.trim()) return;

    try {
      const response = await fetch(`/api/exercices/quiz-grammaire/generiques/${editingGenId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editGenForm),
      });
      const data = await response.json();

      if (data.success) {
        setEditingGenId(null);
        loadData();
      } else {
        alert('Erreur : ' + (data.error || 'Erreur inconnue'));
      }
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  // ========== Association Thème → Générique ==========

  const handleChangeGenerique = async (themeId, generiqueId) => {
    try {
      const response = await fetch(`/api/exercices/quiz-grammaire/themes/${themeId}/generique`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ generiqueId: generiqueId || null }),
      });
      const data = await response.json();

      if (data.success) {
        loadData();
      } else {
        alert('Erreur : ' + (data.error || 'Erreur inconnue'));
      }
    } catch (err) {
      alert('Erreur : ' + err.message);
    }
  };

  // ========== Groupement des thèmes ==========

  const themesWithout = themes.filter(t => !t.generique);
  const themesByGenerique = {};

  themes.forEach(t => {
    if (t.generique) {
      const gId = t.generique.id;
      if (!themesByGenerique[gId]) {
        themesByGenerique[gId] = {
          generique: t.generique,
          themes: [],
        };
      }
      themesByGenerique[gId].themes.push(t);
    }
  });

  // ========== RENDU ==========

  return (
    <div className="themes-generiques-page">
      <div className="page-header">
        <h1>
          <LinkIcon size={32} color="#4F46E5" />
          Themes / Generiques
        </h1>
        <p className="page-description">
          Associer les themes de grammaire a leurs categories generiques.
        </p>
      </div>

      {/* Sélecteur de langue */}
      <div className="controls-bar">
        <div className="langue-selector">
          {['fr', 'en', 'es'].map(l => (
            <button
              key={l}
              className={`btn-langue ${langue === l ? 'active' : ''}`}
              onClick={() => setLangue(l)}
            >
              {getLangueFlag(l)} {l.toUpperCase()}
            </button>
          ))}
        </div>
        <button
          className="btn-create-generique"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          <Plus size={16} /> Nouveau generique
        </button>
      </div>

      {/* Formulaire de création générique */}
      {showCreateForm && (
        <div className="create-generique-form">
          <div className="form-row">
            <input
              type="text"
              placeholder="Nom du generique"
              value={createForm.generique}
              onChange={(e) => setCreateForm({ ...createForm, generique: e.target.value })}
              className="input-generique"
            />
            <input
              type="text"
              placeholder="Explication (optionnel)"
              value={createForm.explication}
              onChange={(e) => setCreateForm({ ...createForm, explication: e.target.value })}
              className="input-explication"
            />
            <button className="btn-action btn-save" onClick={handleCreateGenerique}>
              <Check size={16} /> Creer
            </button>
            <button className="btn-action btn-cancel" onClick={() => setShowCreateForm(false)}>
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="loading-state">
          <Loader2 size={40} className="spin" />
          <p>Chargement...</p>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="error-state">
          <p>{error}</p>
          <button className="btn-retry" onClick={loadData}>Reessayer</button>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Stats */}
          <div className="stats-bar">
            <span>{themes.length} theme{themes.length > 1 ? 's' : ''}</span>
            <span>{generiques.length} generique{generiques.length > 1 ? 's' : ''}</span>
            <span className={themesWithout.length > 0 ? 'stat-warning' : 'stat-ok'}>
              {themesWithout.length > 0
                ? `${themesWithout.length} sans generique`
                : 'Tous associes'}
            </span>
          </div>

          {/* Section : Thèmes SANS générique */}
          {themesWithout.length > 0 && (
            <div className="section-group section-orphans">
              <div className="section-header">
                <AlertTriangle size={20} color="#F59E0B" />
                <h2>Themes sans generique ({themesWithout.length})</h2>
              </div>
              <div className="themes-list">
                {themesWithout.map(t => (
                  <ThemeRow
                    key={t.id}
                    theme={t}
                    generiques={generiques}
                    onChangeGenerique={handleChangeGenerique}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sections : Par générique */}
          {Object.values(themesByGenerique)
            .sort((a, b) => a.generique.generique.localeCompare(b.generique.generique))
            .map(group => (
              <div key={group.generique.id} className="section-group">
                <div className="section-header">
                  <h2>{group.generique.generique}</h2>
                  <span className="theme-count">{group.themes.length} theme{group.themes.length > 1 ? 's' : ''}</span>
                  {editingGenId === group.generique.id ? (
                    <div className="inline-edit-gen">
                      <input
                        type="text"
                        value={editGenForm.generique}
                        onChange={(e) => setEditGenForm({ ...editGenForm, generique: e.target.value })}
                        className="input-edit-gen"
                      />
                      <input
                        type="text"
                        value={editGenForm.explication}
                        onChange={(e) => setEditGenForm({ ...editGenForm, explication: e.target.value })}
                        placeholder="Explication"
                        className="input-edit-gen"
                      />
                      <button className="btn-action btn-save" onClick={handleSaveGenerique}>
                        <Check size={14} />
                      </button>
                      <button className="btn-action btn-cancel" onClick={() => setEditingGenId(null)}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-action btn-edit-gen"
                      onClick={() => startEditingGenerique(group.generique)}
                      title="Modifier le generique"
                    >
                      <Edit2 size={14} />
                    </button>
                  )}
                </div>
                {group.generique.explication && (
                  <p className="generique-explication">{group.generique.explication}</p>
                )}
                <div className="themes-list">
                  {group.themes.map(t => (
                    <ThemeRow
                      key={t.id}
                      theme={t}
                      generiques={generiques}
                      onChangeGenerique={handleChangeGenerique}
                    />
                  ))}
                </div>
              </div>
            ))}
        </>
      )}
    </div>
  );
}

// ========== Composant ligne thème ==========
function ThemeRow({ theme, generiques, onChangeGenerique }) {
  return (
    <div className="theme-row">
      <div className="theme-info">
        <span className="theme-name">{theme.theme}</span>
        <span className="theme-grammar">{theme.pointGrammaire}</span>
        <span className={`badge-niveau badge-${theme.niveauDifficulte}`}>
          {theme.niveauDifficulte}
        </span>
        <span className="theme-questions">{theme.nbQuestions} q.</span>
      </div>
      <div className="theme-generique-select">
        <select
          value={theme.generique ? theme.generique.id : ''}
          onChange={(e) => onChangeGenerique(theme.id, e.target.value ? parseInt(e.target.value) : null)}
        >
          <option value="">-- Aucun generique --</option>
          {generiques.map(g => (
            <option key={g.id} value={g.id}>{g.generique}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
